import React, { useEffect, useRef } from 'react';

function NeuralNetwork({ nodeCount = 50, interactive = true, className = '' }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;
    let nodes = [];
    let mouse = { x: null, y: null };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const initNodes = () => {
      nodes = [];
      const width = canvas.offsetWidth;
      const height = canvas.offsetHeight;
      for (let i = 0; i < nodeCount; i++) {
        nodes.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          radius: Math.random() * 2 + 1.5,
          pulseOffset: Math.random() * Math.PI * 2,
        });
      }
    };

    const handleMouseMove = (e) => {
      // Get the parent container (section) to calculate relative position
      const container = canvas.parentElement;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      mouse = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const handleMouseLeave = () => {
      mouse = { x: null, y: null };
    };

    const animate = () => {
      // Use container dimensions to match mouse position calculation
      const container = canvas.parentElement;
      const width = container ? container.offsetWidth : canvas.offsetWidth;
      const height = container ? container.offsetHeight : canvas.offsetHeight;
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      const time = Date.now() * 0.001;

      nodes.forEach((node, i) => {
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < 0 || node.x > width) node.vx *= -1;
        if (node.y < 0 || node.y > height) node.vy *= -1;
        node.x = Math.max(0, Math.min(width, node.x));
        node.y = Math.max(0, Math.min(height, node.y));

        const pulse = Math.sin(time * 2 + node.pulseOffset) * 0.3 + 0.7;
        let nearMouse = false;
        let mouseDistance = Infinity;

        if (mouse.x !== null && interactive) {
          mouseDistance = Math.hypot(node.x - mouse.x, node.y - mouse.y);
          // Larger radius so nodes react from farther away
          nearMouse = mouseDistance < 250;
        }

        // Draw connections (limited per node for performance)
        let connections = 0;
        const maxConnections = 5;
        const connectionThreshold = 120;

        for (let j = i + 1; j < nodes.length; j++) {
          const other = nodes[j];
          const dist = Math.hypot(other.x - node.x, other.y - node.y);
          if (dist < connectionThreshold) {
            let connectionNearMouse = false;
            if (mouse.x !== null && interactive) {
              const midX = (node.x + other.x) / 2;
              const midY = (node.y + other.y) / 2;
              // Slightly larger radius for lighting connections
              connectionNearMouse = Math.hypot(midX - mouse.x, midY - mouse.y) < 180;
            }
            const opacity = (1 - dist / connectionThreshold) * (connectionNearMouse ? 0.7 : 0.18);
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = connectionNearMouse
              ? `rgba(0, 180, 216, ${opacity})`
              : `rgba(100, 140, 180, ${opacity})`;
            ctx.lineWidth = connectionNearMouse ? 1.8 : 1.2;
            ctx.stroke();
            connections++;
            if (connections >= maxConnections) break;
          }
        }

        // Draw node
        const nodeOpacity = nearMouse ? 1.0 : 0.4 * pulse;
        const nodeColor = nearMouse ? '0, 180, 216' : '100, 140, 180';
        const glowRadius = nearMouse ? node.radius * 3 : node.radius * 1.5;

        const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, glowRadius);
        gradient.addColorStop(0, `rgba(${nodeColor}, ${nodeOpacity})`);
        gradient.addColorStop(1, `rgba(${nodeColor}, 0)`);
        ctx.beginPath();
        ctx.arc(node.x, node.y, glowRadius, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * (nearMouse ? 1.3 : 1), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${nodeColor}, ${nearMouse ? 1 : 0.6})`;
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    resize();
    initNodes();
    animate();

    if (interactive) {
      // Listen to mouse events on the parent container so it works even when hovering over text
      const container = canvas.parentElement;
      if (container) {
        container.addEventListener('mousemove', handleMouseMove);
        container.addEventListener('mouseleave', handleMouseLeave);
      }
    }
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
      if (interactive) {
        const container = canvas.parentElement;
        if (container) {
          container.removeEventListener('mousemove', handleMouseMove);
          container.removeEventListener('mouseleave', handleMouseLeave);
        }
      }
    };
  }, [nodeCount, interactive]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none', // Canvas doesn't need pointer events, parent handles it
      }}
    />
  );
}

export default NeuralNetwork;